/**
 * Turns an Articulate Rise 360 web export into an article draft.
 *
 * A Rise export's index.html is an empty shell - the player renders the
 * course from `runtime-data.js`, which holds the whole course as base64 JSON
 * (`__jsonp("runtime-data.js", "<base64>")`). This module reads that data and
 * writes the lessons as Markdown: text blocks become headings and paragraphs,
 * pictures, galleries, steps, lists and quotes keep their shape, and every
 * picture, video or audio file is taken from the export's `assets/` folder.
 *
 * The media are not uploaded here. Each one becomes a placeholder in the text
 * plus a File in `files`; the editor stages them like pictures the author
 * inserted by hand, so they are uploaded only when the article is saved.
 * Very large pictures are scaled down first.
 *
 * `source` is a Map from path to { size, read(): Promise<Blob> } - built from
 * a picked folder or from a ZIP (see readZip).
 */

import { readZip } from './zipReader';

const MAX_IMAGE_WIDTH = 2400;
const MAX_IMAGE_BYTES = 1.5 * 1024 * 1024;

const MIME_BY_EXTENSION = {
  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  gif: 'image/gif',
  webp: 'image/webp',
  mp4: 'video/mp4',
  webm: 'video/webm',
  mov: 'video/quicktime',
  m4v: 'video/mp4',
  mp3: 'audio/mpeg',
  m4a: 'audio/mp4',
  wav: 'audio/wav',
  ogg: 'audio/ogg',
};

function extensionOf(name) {
  const match = /\.([a-z0-9]+)$/i.exec(name);
  return match ? match[1].toLowerCase() : '';
}

function baseName(path) {
  return path.split('/').pop();
}

function safeDecode(value) {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

// Pixel area of a picture, for picking the main views of an interaction;
// the file size stands in where the browser cannot decode it.
async function pictureArea(entry) {
  try {
    const bitmap = await createImageBitmap(await entry.read());
    const area = bitmap.width * bitmap.height;
    bitmap.close();
    return area;
  } catch {
    return entry.size;
  }
}

// ---------------------------------------------------------------------------
// HTML (Rise's rich text) to Markdown

// "<strong>Zielvorgang </strong>und" must stay "**Zielvorgang** und": the
// markers go round the words, the spaces stay outside them.
function wrapKeepingSpaces(text, marker) {
  const match = /^(\s*)([\s\S]*?)(\s*)$/.exec(text);

  return match[2] ? `${match[1]}${marker}${match[2]}${marker}${match[3]}` : text;
}

function inlineMarkdown(node) {
  if (node.nodeType === Node.TEXT_NODE) {
    return node.textContent.replace(/\s+/g, ' ');
  }

  if (node.nodeType !== Node.ELEMENT_NODE) {
    return '';
  }

  const inner = Array.from(node.childNodes).map(inlineMarkdown).join('');
  const tag = node.tagName.toLowerCase();

  switch (tag) {
    case 'br':
      return '\n';
    case 'strong':
    case 'b':
      return wrapKeepingSpaces(inner, '**');
    case 'em':
    case 'i':
      return wrapKeepingSpaces(inner, '*');
    case 'a': {
      const href = node.getAttribute('href') || '';
      return /^https?:\/\//i.test(href) && inner.trim()
        ? `[${inner.trim()}](${href})`
        : inner;
    }
    default:
      return inner;
  }
}

function blockMarkdown(node, out) {
  if (node.nodeType === Node.TEXT_NODE) {
    const text = node.textContent.replace(/\s+/g, ' ').trim();
    if (text) {
      out.push(text);
    }
    return;
  }

  if (node.nodeType !== Node.ELEMENT_NODE) {
    return;
  }

  const tag = node.tagName.toLowerCase();

  if (tag === 'ul' || tag === 'ol') {
    const lines = Array.from(node.children)
      .filter((child) => child.tagName.toLowerCase() === 'li')
      .map((li, index) => `${tag === 'ol' ? `${index + 1}.` : '-'} ${inlineMarkdown(li).trim()}`)
      .filter((line) => !/^(-|\d+\.)\s*$/.test(line));
    if (lines.length) {
      out.push(lines.join('\n'));
    }
    return;
  }

  if (/^h[1-6]$/.test(tag)) {
    const text = inlineMarkdown(node).trim();
    if (text) {
      out.push(`### ${text}`);
    }
    return;
  }

  if (tag === 'blockquote') {
    const text = inlineMarkdown(node).trim();
    if (text) {
      out.push(text.split('\n').map((line) => `> ${line}`).join('\n'));
    }
    return;
  }

  if (tag === 'p') {
    const text = inlineMarkdown(node).replace(/[ \t]+\n/g, '\n').trim();
    if (text) {
      out.push(text);
    }
    return;
  }

  Array.from(node.childNodes).forEach((child) => blockMarkdown(child, out));
}

export function htmlToMarkdown(html) {
  if (!html) {
    return '';
  }

  const doc = new DOMParser().parseFromString(`<div>${html}</div>`, 'text/html');
  const out = [];
  blockMarkdown(doc.body.firstChild, out);
  return out.join('\n\n');
}

export function htmlToText(html) {
  if (!html) {
    return '';
  }

  const doc = new DOMParser().parseFromString(html, 'text/html');
  return doc.body.textContent.replace(/\s+/g, ' ').trim();
}

// ---------------------------------------------------------------------------
// Pictures

async function downscaleImage(blob, name, type) {
  if (!type.startsWith('image/') || type === 'image/gif') {
    return null;
  }

  let bitmap;

  try {
    bitmap = await createImageBitmap(blob);
  } catch {
    return null;
  }

  if (bitmap.width <= MAX_IMAGE_WIDTH && blob.size <= MAX_IMAGE_BYTES) {
    bitmap.close();
    return null;
  }

  const scale = Math.min(1, MAX_IMAGE_WIDTH / bitmap.width);
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(bitmap.width * scale);
  canvas.height = Math.round(bitmap.height * scale);

  const context = canvas.getContext('2d');

  if (!context) {
    bitmap.close();
    return null;
  }

  // JPEG has no transparency: put the picture on white.
  context.fillStyle = '#ffffff';
  context.fillRect(0, 0, canvas.width, canvas.height);
  context.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
  bitmap.close();

  const scaled = await new Promise((resolve) => {
    canvas.toBlob(resolve, 'image/jpeg', 0.85);
  });

  if (!scaled) {
    return null;
  }

  return new File([scaled], name.replace(/\.[^.]+$/, '') + '.jpg', { type: 'image/jpeg' });
}

// ---------------------------------------------------------------------------
// The course

function decodeRuntimeData(scriptText) {
  const match = /__jsonp\(\s*["']runtime-data\.js["']\s*,\s*["']([^"']+)["']\s*\)/.exec(scriptText);

  if (!match) {
    throw new Error('runtime-data.js enthält keine Kursdaten.');
  }

  const binary = atob(match[1]);
  const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));
  return JSON.parse(new TextDecoder('utf-8').decode(bytes));
}

const RUNTIME_DATA = 'runtime-data.js';

// macOS adds "__MACOSX/" and "._name" resource-fork files to ZIPs it makes.
function isJunk(path) {
  return path.startsWith('__MACOSX/') || path.includes('/__MACOSX/') || baseName(path).startsWith('._');
}

function isRuntimeData(path) {
  return !isJunk(path) && (path === RUNTIME_DATA || path.endsWith(`/${RUNTIME_DATA}`));
}

/** Finds the course in a picked folder or ZIP: the shortest runtime-data.js. */
function findRuntimeData(source) {
  const candidates = Array.from(source.keys())
    .filter(isRuntimeData)
    .sort((a, b) => a.length - b.length);

  if (!candidates.length) {
    throw new Error(
      'Das ist kein Rise-Export: Die Datei runtime-data.js wurde nicht gefunden.',
    );
  }

  return candidates[0];
}

export async function importRiseCourse(source) {
  const dataPath = findRuntimeData(source);
  const root = dataPath.slice(0, dataPath.length - 'runtime-data.js'.length);
  const data = decodeRuntimeData(await (await source.get(dataPath).read()).text());
  const course = data.course ?? {};

  const files = [];
  const warnings = [];
  let placeholderCount = 0;

  function assetEntry(key) {
    if (!key) {
      return null;
    }

    const decoded = safeDecode(key);

    return source.get(`${root}assets/${decoded}`) ?? source.get(`${root}assets/${key}`) ?? null;
  }

  async function stageFile(entry, path, kind) {
    const name = baseName(path);
    const type = MIME_BY_EXTENSION[extensionOf(name)];

    if (!type || !type.startsWith(`${kind}/`)) {
      warnings.push(`Datei übersprungen (Format nicht unterstützt): ${name}`);
      return null;
    }

    const blob = await entry.read();
    let file = new File([blob], name, { type });

    if (kind === 'image') {
      const scaled = await downscaleImage(blob, name, type);
      if (scaled) {
        file = scaled;
      }
    }

    placeholderCount += 1;
    const placeholder = `rise-import://${placeholderCount}/${encodeURIComponent(file.name)}`;
    files.push({ placeholder, file, type: kind });
    return placeholder;
  }

  async function mediaMarkdown(media, alt = '') {
    if (!media) {
      return '';
    }

    for (const kind of ['image', 'video', 'audio']) {
      const key = media[kind]?.key;

      if (!key) {
        continue;
      }

      const entry = assetEntry(key);

      if (!entry) {
        warnings.push(`Datei im Export nicht gefunden: ${safeDecode(key)}`);
        return '';
      }

      const placeholder = await stageFile(entry, safeDecode(key), kind);

      if (!placeholder) {
        return '';
      }

      const label = alt || (kind === 'image' ? 'Abbildung' : baseName(safeDecode(key)).replace(/\.[^.]+$/, ''));
      return kind === 'image'
        ? `![${label.replace(/[[\]]/g, '')}](${placeholder})`
        : `[${label.replace(/[[\]]/g, '')}](${placeholder})`;
    }

    return '';
  }

  // Storyline interactions cannot live in an article. Their two largest
  // pictures become a before/after slider (":::vergleich", see
  // utils/compareBlocks.js) - what the Storyline comparison sliders Rise
  // courses embed actually show; a single picture is inserted as it is.
  async function storylineMarkdown(media) {
    const src = media?.storyline?.src ?? '';
    const folder = src.split('/')[0];
    const title = media?.storyline?.meta?.title;
    const prefix = `${root}assets/${folder}/mobile/`;
    const candidates = Array.from(source.entries())
      .filter(([path]) => path.startsWith(prefix) && MIME_BY_EXTENSION[extensionOf(path)]?.startsWith('image/'));
    const areas = await Promise.all(candidates.map(([, entry]) => pictureArea(entry)));
    const pictures = candidates
      .map((candidate, index) => ({ candidate, area: areas[index] }))
      .sort((a, b) => b.area - a.area || b.candidate[1].size - a.candidate[1].size)
      .slice(0, 2)
      .map(({ candidate }) => candidate);

    const placeholders = [];

    for (const [path, entry] of pictures) {
      const placeholder = await stageFile(entry, path, 'image');
      if (placeholder) {
        placeholders.push(placeholder);
      }
    }

    const name = title ? ` „${title}“` : '';

    if (placeholders.length === 2) {
      warnings.push(
        `Interaktiver Storyline-Block${name} als Vorher/Nachher-Vergleich übernommen - `
        + 'bitte prüfen, ob links und rechts richtig herum sind.',
      );
      return [':::vergleich', ...placeholders.map((p) => `![](${p})`), ':::'].join('\n');
    }

    warnings.push(`Interaktiver Storyline-Block${name} kann nicht übernommen werden`
      + (placeholders.length ? '; stattdessen ein Bild daraus.' : '.'));
    return placeholders.map((p) => `![Ansicht aus dem interaktiven Element](${p})`).join('\n\n');
  }

  async function blockMarkdown(block, headingLevel) {
    const h = '#'.repeat(headingLevel);
    const sub = '#'.repeat(Math.min(headingLevel + 1, 4));
    const parts = [];
    const items = (block.items ?? []).filter((item) => !item.isHidden);

    switch (block.type) {
      case 'text':
        for (const item of items) {
          const heading = htmlToText(item.heading);
          if (heading) {
            parts.push(`${block.variant === 'subheading' ? sub : h} ${heading}`);
          }
          const paragraph = htmlToMarkdown(item.paragraph);
          if (paragraph) {
            parts.push(block.family === 'statement' || block.family === 'note'
              ? paragraph.split('\n').map((line) => `> ${line}`).join('\n')
              : paragraph);
          }
        }
        break;

      case 'image':
      case 'gallery':
        for (const item of items) {
          const caption = htmlToText(item.caption);
          const picture = await mediaMarkdown(item.media, caption);
          if (picture) {
            parts.push(picture);
          }
          if (caption) {
            parts.push(caption);
          }
          const paragraph = htmlToMarkdown(item.paragraph);
          if (paragraph) {
            parts.push(paragraph);
          }
        }
        break;

      case 'multimedia':
        for (const item of items) {
          const media = await mediaMarkdown(item.media, htmlToText(item.caption) || htmlToText(item.title));
          if (media) {
            parts.push(media);
          } else if (item.media?.embed) {
            warnings.push('Eingebetteter Inhalt (Embed) wurde nicht übernommen.');
          }
          const paragraph = htmlToMarkdown(item.paragraph || item.caption);
          if (paragraph) {
            parts.push(paragraph);
          }
        }
        break;

      case 'list': {
        const lines = items
          .map((item, index) => {
            const text = htmlToMarkdown(item.paragraph).replace(/\n+/g, ' ');
            if (!text) return null;
            if (block.variant?.includes('numbered')) return `${index + 1}. ${text}`;
            if (block.variant?.includes('checkbox')) return `- [ ] ${text}`;
            return `- ${text}`;
          })
          .filter(Boolean);
        if (lines.length) {
          parts.push(lines.join('\n'));
        }
        break;
      }

      case 'quote':
        for (const item of items) {
          const text = htmlToMarkdown(item.paragraph);
          if (text) {
            parts.push(text.split('\n').map((line) => `> ${line}`).join('\n'));
          }
          const name = htmlToText(item.name);
          if (name) {
            parts.push(`> — ${name}`);
          }
        }
        break;

      case 'divider':
        break;

      case 'interactive':
        if (block.family === '360' || block.variant === 'storyline') {
          const media = block.items?.[0]?.media;
          parts.push(await storylineMarkdown(media));
          break;
        }

        {
          // A tabs block stays tabs (":::tabs", see utils/tabBlocks.js); the
          // other kinds (accordion, process...) become one section per item.
          // Within an item the text comes first and the picture below it,
          // as Rise shows them.
          const isTabs = block.variant === 'tabs';
          if (isTabs) {
            parts.push(':::tabs');
          }

          let step = 0;
          for (const item of items) {
            const isStep = block.variant === 'process' && item.type === 'step';
            const title = htmlToText(item.title);
            if (isStep) {
              step += 1;
              parts.push(`${sub} Schritt ${step}${title ? `: ${title}` : ''}`);
            } else if (title || isTabs) {
              parts.push(`${sub} ${title || `Reiter ${items.indexOf(item) + 1}`}`);
            }
            const description = htmlToMarkdown(item.description);
            if (description) {
              parts.push(description);
            }
            const picture = await mediaMarkdown(item.media, title || (isStep ? `Schritt ${step}` : ''));
            if (picture) {
              parts.push(picture);
            }
          }

          if (isTabs) {
            parts.push(':::');
          }
        }
        break;

      case 'knowledgeCheck':
        warnings.push('Ein Wissenscheck (Quiz) wurde nicht übernommen.');
        break;

      default: {
        const text = items
          .map((item) => [item.heading, item.title, item.paragraph, item.description]
            .filter(Boolean).map(htmlToMarkdown).join('\n\n'))
          .filter(Boolean)
          .join('\n\n');
        if (text) {
          parts.push(text);
        }
        warnings.push(`Block-Typ „${block.type}“ nur als Text übernommen.`);
      }
    }

    return parts.join('\n\n');
  }

  const lessons = (course.lessons ?? []).filter((lesson) => lesson.type === 'blocks');
  const skipped = (course.lessons ?? []).length - lessons.length;

  if (skipped > 0) {
    warnings.push(`${skipped} Lektion(en) ohne Inhaltsblöcke (z. B. Quiz) wurden übersprungen.`);
  }

  // One lesson carrying the course's own title: its blocks are the article.
  // Several lessons: each becomes a section, with its blocks one level down.
  const single = lessons.length === 1;
  const sections = [];

  for (const lesson of lessons) {
    const blocks = [];

    if (!single && lesson.title) {
      blocks.push(`## ${htmlToText(lesson.title)}`);
    }

    for (const block of lesson.items ?? []) {
      const markdown = await blockMarkdown(block, single ? 2 : 3);
      if (markdown) {
        blocks.push(markdown);
      }
    }

    sections.push(blocks.join('\n\n'));
  }

  // The course description often ends with a "click Start" line meant for
  // the player; the first paragraph is the summary.
  const description = new DOMParser().parseFromString(course.description ?? '', 'text/html');
  const firstParagraph = Array.from(description.querySelectorAll('p'))
    .map((p) => p.textContent.replace(/\s+/g, ' ').trim())
    .find(Boolean) ?? htmlToText(course.description);

  return {
    title: htmlToText(course.title) || 'Importierter Kurs',
    summary: (firstParagraph ?? '').slice(0, 1000),
    text: sections.join('\n\n').replace(/\n{3,}/g, '\n\n').trim(),
    files,
    warnings,
  };
}

/**
 * All Rise courses in a picked folder or ZIP. One export is one course; a
 * folder or ZIP may also hold several - as course folders side by side, or as
 * one ZIP per course (also inside a ZIP). Every runtime-data.js that is not
 * inside another course marks a course.
 *
 * Returns [{ name, title, open() }]: `open()` resolves to that course's own
 * source for importRiseCourse. A course from a nested ZIP is unpacked again
 * when opened instead of being held in memory between finding and importing,
 * so a ZIP of many large courses never sits in the page all at once.
 */
export async function findRiseCourses(source) {
  return discoverCourses(source, '', async () => source);
}

async function discoverCourses(source, label, reopen) {
  const paths = Array.from(source.keys()).filter((path) => !isJunk(path));
  const roots = paths
    .filter(isRuntimeData)
    .map((path) => path.slice(0, path.length - RUNTIME_DATA.length))
    .sort((a, b) => a.length - b.length)
    .filter((root, index, all) => !all.slice(0, index).some((outer) => root.startsWith(outer)));
  const insideCourse = (path) => roots.some((root) => path.startsWith(root));
  const courses = [];

  for (const root of roots) {
    let title = '';
    try {
      const data = decodeRuntimeData(await (await source.get(`${root}${RUNTIME_DATA}`).read()).text());
      title = htmlToText(data.course?.title);
    } catch {
      // Left to importRiseCourse, which reports it for this course alone.
    }

    const name = root.replace(/\/$/, '').split('/').pop() || label;

    courses.push({
      name,
      title: title || name || 'Importierter Kurs',
      async open() {
        const all = await reopen();
        const own = new Map();
        for (const [path, entry] of all) {
          if (path.startsWith(root)) {
            own.set(path, entry);
          }
        }
        return own;
      },
    });
  }

  const zips = paths.filter((path) => /\.zip$/i.test(path) && !insideCourse(path)).sort();

  for (const path of zips) {
    const openInner = async () => readZip(await (await reopen()).get(path).read());
    courses.push(...await discoverCourses(
      await openInner(),
      baseName(path).replace(/\.zip$/i, ''),
      openInner,
    ));
  }

  return courses;
}

/** A source for importRiseCourse from files picked with a folder input. */
export function sourceFromFileList(fileList) {
  const source = new Map();

  for (const file of Array.from(fileList)) {
    const path = file.webkitRelativePath || file.name;
    source.set(path, { size: file.size, read: async () => file });
  }

  return source;
}
